import React, {useState, useEffect, useCallback} from 'react';
import {EZPRTheme} from '../../_theme';
import PropTypes from 'prop-types';
import {createFilterOptions, createTheme} from '@mui/material';
import {Autocomplete, CircularProgress, TextField, ThemeProvider, Popper, ButtonGroup, Button} from '@mui/material';
import {makeStyles} from '@mui/styles';
import {bgColorRequester, bgColorSpender, ezprOrange, useDebounce} from '../../_helpers';
import {getTaxCodeLambda, matomoTrackEventLambda} from '../../../utils/functionSignatures';
import {requestStores} from '../../ListOfPREditors.brick.jsx';
import {TooltipWrapperIssue} from '../../TooltipWrapperIssue.jsx';
import {ErrorFlow, BrickContext} from 'olympe';
import {EMPTY_MATERIAL_NUMBER, LAMBDA_NAMES} from '../../../utils/constants';

/**
 * @enum {string}
 */
TaxCodeAutomation.tabs = {
    SUGGESTION: 'Suggestion',
    ALL: 'All',
};

/**
 * @param {TaxCodeAutomation.propTypes} props
 * @return {JSX.Element}
 */
export function TaxCodeAutomation(props) {
    const style = {
        width: '100%',
        height: '100%',
    };

    const id = 'id-' + (Date.now() * Math.random());

    const _theme = createTheme();
    const useStyles = makeStyles(
        () => {
            return {
                root: {
                    '& .css-qzbt6i-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderLeft: props.error ? 'solid 1px rgba(255,0,0,1.0)' : 'solid 1px rgba(0,0,0,0.54)',
                        borderRadius: '0',
                    },
                    '& .css-113ntv0-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-popupIndicator': {
                        borderRight: props.error ? 'solid 1px rgba(255,0,0,1.0)' : 'solid 1px rgba(0,0,0,0.54)',
                        borderRadius: '0',
                    },
                    '& .css-1glvl0p-MuiButtonBase-root-MuiIconButton-root-MuiAutocomplete-clearIndicator': {
                        marginRight: '3px',
                        borderRadius: '0',
                    },
                },
            };
        },
        {_theme},
    );
    const classes = useStyles();

    // A query is performed
    // - when the user opens the autocomplete
    const [isPerformingQuery, setIsPerformingQuery] = useState(false);
    const [open, setOpen] = useState(false);
    const [textInput, setTextInput] = useState('');
    const debouncedTextInput = useDebounce(textInput, 800);
    const [queriedText, setQueriedText] = useState('');
    const [options, setOptions] = useState([]);
    const [currentTab, setCurrentTab] = useState(TaxCodeAutomation.tabs.SUGGESTION);

    const useRequestStore = requestStores.get(props.prTag);

    const wayOfBuying = useRequestStore((state) => state.wayOfBuying);
    const supplierCode = useRequestStore((state) => state.vendorCode);
    const purchaseOrg = useRequestStore((state) => state.purchaseOrganization);
    const companyCode = useRequestStore((state) => state.companyCode);
    const userIdToCallSAPAPIs = useRequestStore((state) => state.userIdToCallSAPAPIs);

    const [automationEnabled, setAutomationEnabled] = useState(true);
    const [showErrorTooltip, setShowErrorTooltip] = useState(false);
    const [error, setError] = useState({code: 200, body: ''});


    const matomoTrackEvent = props.context.get(LAMBDA_NAMES.MATOMO_TRACK_EVENT);
    const getTaxCodeAutomation = props.context.get(LAMBDA_NAMES.GET_TAX_CODE);

    // This useEffect handles the queries to perform to get the options for the autocomplete
    useEffect(() => {
        // avoid performing several queries simultaneously
        if (isPerformingQuery) {
            return;
        }

        const searchInput = debouncedTextInput.trim();

        // avoid performing a query of with the same input as the last query
        if (queriedText === searchInput && options.length > 0) {
            return;
        }

        // avoid performing a query when the autocomplete is closed
        if (open === false) {
            return;
        }
        // start a query
        setIsPerformingQuery(true);

        (async () => {
            const today = new Date();
            const oneYearAgo = new Date(new Date().setFullYear(today.getFullYear() - 1));
            const useAutomation = currentTab === TaxCodeAutomation.tabs.SUGGESTION;

            let materialNumber;
            const extractedMaterialNumber = props.material.name.split(' - ')[0];
            if (extractedMaterialNumber.length === EMPTY_MATERIAL_NUMBER) {
                materialNumber = '';
            } else {
                materialNumber = extractedMaterialNumber.padStart(18, '0');
            }

            getTaxCodeLambda(
                props.context,
                getTaxCodeAutomation,
                useAutomation,
                wayOfBuying.currentValue.value,
                supplierCode.toString().padStart(10, '0'),
                purchaseOrg.value,
                companyCode.value,
                oneYearAgo.toLocaleDateString('en-US'),
                today.toLocaleDateString('en-US'),
                materialNumber,
                searchInput,
                userIdToCallSAPAPIs,
                props.material.l3Category,
                (result) => {
                    let data;
                    try {
                        data = JSON.parse(result);
                    } catch (error) {
                        data = result;
                    }
                    if (currentTab === TaxCodeAutomation.tabs.SUGGESTION) {
                        if (data.length === 1) {
                            handleChange(undefined, data[0]);
                        }
                        setOptions(data);
                        setQueriedText(searchInput);
                    } else if (currentTab === TaxCodeAutomation.tabs.ALL) {
                        const formattedData = data.map((item) => {
                            return {
                                label: `${item.PurchsCode} - ${item.NameVAT}`,
                                value: item.PurchsCode,
                            };
                        }).filter((value, index, self) => // Remove duplicates
                            index === self.findIndex((t) => {
                                return t.label === value.label;
                            },
                            ),
                        );
                        setOptions(formattedData);
                        setQueriedText(searchInput);
                    }
                    setIsPerformingQuery(false);
                },
                (err) => {
                    if (err instanceof ErrorFlow) {
                        setError({code: err.getCode(), body: err.getMessage()});
                    } else {
                        setError({code: 1, body: err});
                    }
                    setShowErrorTooltip(true);
                    setIsPerformingQuery(false);
                },
            );
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, currentTab, debouncedTextInput, queriedText]);

    useEffect(() => {
        if (!open) {
            setOptions([]);
        }
    }, [open]);

    // This useEffect enable/disabled the tax code automation based on the company code (US market doesn't have access
    // to this feature
    useEffect(() => {
        const usMarket = companyCode.value.startsWith('US');
        setAutomationEnabled(!usMarket);
        if (usMarket) {
            setCurrentTab(TaxCodeAutomation.tabs.ALL);
        }
    }, [companyCode]);

    const filterOptions = createFilterOptions({
        limit: 50,
    });

    const handleChange = (_, newValue) => {
        if (!newValue) {
            setTextInput('');
            props.onChange({label: '', value: ''});
        } else {
            props.onChange(newValue);
            if (automationEnabled) {
                matomoTrackEventLambda(props.context, matomoTrackEvent, 'dropdown', 'taxCode', currentTab, undefined, () => {
                });
            }
        }
    };

    const handleChangeTab = (tabName) => {
        setCurrentTab(tabName);
        setIsPerformingQuery(false);
        setOptions([]);
    };
    const taxCodeAutomationPopper = useCallback((params) => {
        return (
            <Popper {...params} style={{maxWidth: '400px'}} className='custom-dropdown-menu-with-buttons'>
                <ButtonGroup color="primary" aria-label="outlined primary button group">
                    <Button
                        variant='text'
                        onClick={() => {
                            handleChangeTab(TaxCodeAutomation.tabs.SUGGESTION);
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{color: currentTab === TaxCodeAutomation.tabs.SUGGESTION ? ezprOrange : '#CCC'}}
                    >
                        {TaxCodeAutomation.tabs.SUGGESTION}
                    </Button>
                    <Button
                        data-cy={'tax_code_autocomplete_all_btn'}
                        variant='text'
                        onClick={() => {
                            handleChangeTab(TaxCodeAutomation.tabs.ALL);
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{color: currentTab === TaxCodeAutomation.tabs.ALL ? ezprOrange : '#CCC'}}
                    >
                        {TaxCodeAutomation.tabs.ALL}
                    </Button>
                </ButtonGroup>
                {params.children}
            </Popper>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTab]);

    const handleInputChange = (event) => {
        if (currentTab === TaxCodeAutomation.tabs.SUGGESTION) {
            setCurrentTab(TaxCodeAutomation.tabs.ALL);
        }

        const inputValue = event.target.value;
        setTextInput(inputValue);
    };

    // Element
    return (
        <ThemeProvider theme={EZPRTheme}>
            <div className={classes.root}>
                <TooltipWrapperIssue
                    open={showErrorTooltip}
                    onClose={() => setShowErrorTooltip(false)}
                    error={error}
                >
                    <Autocomplete
                        data-cy={'tax_code_autocomplete'}
                        sx={{
                            backgroundColor: props.mandatoryForSpender && props.highlightMandatoryFieldForSpender ?
                                bgColorSpender :
                                props.mandatoryForRequester && props.highlightMandatoryFieldForRequester ?
                                    bgColorRequester :
                                    null,
                        }}
                        id={id}
                        filterOptions={filterOptions}
                        autoSelect={props.autoSelect}
                        disabled={props.disabled}
                        PopperComponent={automationEnabled ? taxCodeAutomationPopper : undefined}
                        options={options}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.label === value.label}
                        fullWidth
                        loading={isPerformingQuery || props.loading}
                        open={open}
                        onOpen={() => {
                            setOpen(true);
                        }}
                        onClose={() => {
                            setOpen(false);
                            setIsPerformingQuery(false);
                        }}
                        style={style}
                        size={'small'}
                        value={props.selectedValue}
                        onChange={handleChange}
                        renderInput={(params) => (
                            <TextField
                                error={props.error}
                                {...params}
                                variant={'outlined'}
                                rows={1}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <React.Fragment>
                                            {isPerformingQuery ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.startAdornment}
                                        </React.Fragment>
                                    ),
                                }}
                                onChange={handleInputChange}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} data-cy={'tax_code_option'}>
                                {option.label}
                                {option.count && <span className='custom-dropdown-item-count'> (used {option.count} times)</span>}
                            </li>
                        )}
                    />
                </TooltipWrapperIssue>
            </div>
        </ThemeProvider>
    );
}

TaxCodeAutomation.propTypes = {
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    prTag: PropTypes.string.isRequired,
    selectedValue: PropTypes.object,
    autoSelect: PropTypes.bool,
    material: PropTypes.object.isRequired,
    onChange: PropTypes.func,
    mandatoryForSpender: PropTypes.bool,
    mandatoryForRequester: PropTypes.bool,
    highlightMandatoryFieldForRequester: PropTypes.bool,
    highlightMandatoryFieldForSpender: PropTypes.bool,
    loading: PropTypes.bool,
};
